import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Check, Mail, User } from "lucide-react";
import { ApiError, authCodeRequest, authCodeVerify } from "../api/client";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { cn } from "../lib/utils";

type Step = "email" | "code";

const RESEND_SECONDS = 60;
const CODE_LENGTH = 6;

export function LoginPage() {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const otpResetRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (step !== "code") return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [step]);

  async function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authCodeRequest(email.trim());
      setCode("");
      setSecondsLeft(RESEND_SECONDS);
      setStep("code");
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const submitCode = useCallback(
    async (value: string) => {
      setError(null);
      setSubmitting(true);
      try {
        await authCodeVerify(email.trim(), value);
        // AuthProvider подхватит изменение через onAuthChanged.
      } catch (err) {
        setError(extractMessage(err));
        setCode("");
        otpResetRef.current();
      } finally {
        setSubmitting(false);
      }
    },
    [email],
  );

  async function resend() {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await authCodeRequest(email.trim());
      setCode("");
      setSecondsLeft(RESEND_SECONDS);
      otpResetRef.current();
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function backToEmail() {
    setStep("email");
    setCode("");
    setError(null);
  }

  return (
    <div className="min-h-screen neu-canvas mesh-warm flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm flex flex-col items-center">
        <Logo size="lg" orientation="vertical" className="mb-6" />

        <h1 className="font-display text-2xl font-bold text-center tracking-tight">
          {step === "email" ? "Войти в сервис" : "Проверьте почту"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground text-center">
          {step === "email" ? (
            "Введите email — пришлём одноразовый код"
          ) : (
            <>
              Код отправлен на{" "}
              <span className="font-mono font-medium text-foreground">{email}</span>
            </>
          )}
        </p>

        <Stepper current={step === "email" ? 1 : 2} className="mt-6 mb-6" />

        <div className="w-full neu-raised rounded-2xl p-6">
          {step === "email" ? (
              <form onSubmit={onSubmitEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-xs uppercase tracking-wider text-muted-foreground"
                  >
                    Email
                  </Label>
                  <div className="relative">
                    <User
                      aria-hidden="true"
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                    />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="Корпоративный email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      className="pl-9 h-11 neu-inset border-transparent shadow-none"
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={submitting}
                >
                  <Mail aria-hidden="true" className="mr-2 h-4 w-4" />
                  {submitting ? "Отправка…" : "Получить код"}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="code-0"
                    className="text-xs uppercase tracking-wider text-muted-foreground"
                  >
                    Код из письма
                  </Label>
                  <OtpInput
                    value={code}
                    disabled={submitting}
                    onChange={setCode}
                    onComplete={submitCode}
                    registerReset={(fn) => {
                      otpResetRef.current = fn;
                    }}
                  />
                </div>
                {error && (
                  <p className="text-sm text-destructive text-center">{error}</p>
                )}
                <div className="text-center">
                  {secondsLeft > 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Отправить повторно через {secondsLeft} с
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline disabled:opacity-50"
                      onClick={resend}
                      disabled={submitting}
                    >
                      Отправить повторно
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={backToEmail}
                >
                  ← Изменить email
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function Stepper({
  current,
  className,
}: {
  current: 1 | 2;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start justify-center gap-3", className)}>
      <StepNode index={1} label="Вход" state={current > 1 ? "done" : "active"} />
      <div
        className={cn(
          "h-px w-12 mt-[18px] transition-colors",
          current > 1 ? "bg-accentWarm/60" : "bg-border",
        )}
      />
      <StepNode
        index={2}
        label="Код"
        state={current === 2 ? "active" : "idle"}
      />
    </div>
  );
}

function StepNode({
  index,
  label,
  state,
}: {
  index: number;
  label: string;
  state: "active" | "done" | "idle";
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative">
        {state === "active" && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-accentWarm/40 blur-md"
          />
        )}
        <div
          className={cn(
            "relative w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
            state === "idle" &&
              "bg-muted text-muted-foreground border border-border",
            (state === "active" || state === "done") &&
              "bg-primary text-primary-foreground",
          )}
        >
          {state === "done" ? (
            <Check aria-hidden="true" className="h-4 w-4" />
          ) : (
            index
          )}
        </div>
      </div>
      <span
        className={cn(
          "text-xs",
          state === "active"
            ? "font-medium text-foreground"
            : "text-muted-foreground",
        )}
      >
        {label}
      </span>
    </div>
  );
}

function OtpInput({
  value,
  disabled,
  onChange,
  onComplete,
  registerReset,
}: {
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onComplete: (v: string) => void;
  registerReset: (fn: () => void) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    registerReset(() => {
      refs.current[0]?.focus();
    });
  }, [registerReset]);

  function setDigit(i: number, digit: string) {
    const arr = value.padEnd(CODE_LENGTH, " ").split("");
    arr[i] = digit;
    const next = arr.join("").trimEnd();
    onChange(next);
    if (next.length === CODE_LENGTH && !next.includes(" ")) {
      onComplete(next);
    }
  }

  function handleChange(i: number, raw: string) {
    const digit = raw.replace(/\D/g, "").slice(-1);
    if (!digit) return;
    setDigit(i, digit);
    if (i < CODE_LENGTH - 1) refs.current[i + 1]?.focus();
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (value[i]) {
        const arr = value.split("");
        arr[i] = "";
        onChange(arr.join("").trimEnd());
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        const arr = value.split("");
        arr[i - 1] = "";
        onChange(arr.join("").trimEnd());
        e.preventDefault();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      refs.current[i + 1]?.focus();
      e.preventDefault();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.slice(0, CODE_LENGTH);
    onChange(next);
    const focusIdx = Math.min(next.length, CODE_LENGTH - 1);
    refs.current[focusIdx]?.focus();
    if (next.length === CODE_LENGTH) onComplete(next);
  }

  return (
    <div className="flex justify-between gap-2">
      {Array.from({ length: CODE_LENGTH }).map((_, i) => (
        <input
          key={i}
          id={i === 0 ? "code-0" : undefined}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={value[i] ?? ""}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          autoFocus={i === 0}
          aria-label={`Цифра ${i + 1}`}
          className={cn(
            "h-12 w-11 rounded-md neu-inset border-transparent",
            "text-center text-lg font-mono font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
      ))}
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Неизвестная ошибка";
}
